import type { MatchOfferSchema } from "@teamz/common";
import type { ComponentProps } from "react";

import AddIcon from "@/assets/add.svg";
import CupIcon from "@/assets/cup.svg";
import EditIcon from "@/assets/edit.svg";
import DotsIcon from "@/assets/placeholder-dots.svg";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { matchOfferSchema, playerFFTranking, rankingTypes } from "@teamz/common";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import type { RouterOutputs } from "@/lib/trpc";

import { useMe } from "@/context/user-context";
import { useUserRole } from "@/hooks/use-user-role";
import { useTRPC } from "@/lib/trpc";
import { cn } from "@/lib/utils";

import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Sheet, SheetClose, SheetContent, SheetFooter, SheetForm, SheetHeader, SheetTitle } from "../ui/sheet";
import { ErrorField, RootField, TextField } from "../ui/text-field";

const EmptyButton = ({ ...props }: ComponentProps<"button">) => {
  return (
    <button type="button" className="h-16 w-full rounded-lg bg-neutral-50 p-1" {...props}>
      <div className="flex h-full items-center justify-center rounded-md border border-dashed border-neutral-400 bg-white">
        <AddIcon className="text-orange-500" />
      </div>
    </button>
  );
};

const OfferButton = ({ offer, variant, ...props }: { offer: RouterOutputs["club"]["getClub"]["matchOffers"][number]; variant: "blue" | "purple" } & ComponentProps<"button">) => {
  const { t } = useTranslation(["profile"]);
  const userRole = useUserRole();
  const { user } = useMe();
  return (
    <button
      type="button"
      className={cn(
        "flex h-16 w-full items-center gap-1 rounded-lg p-1",
        variant === "blue" && "bg-blue-200",
        variant === "purple" && "bg-purple-200",
        userRole === "player" && !user.isAdmin && "pointer-events-none",
      )}
      {...props}
    >
      <div className="relative flex h-full flex-1 items-center justify-center rounded-md bg-white">
        <span className="text-sm font-bold text-blue-500">
          {
            (offer.playerRankingType === "FFT" && offer.playerRanking === "T100")
              ? `N${offer.playerRankingNumber}`
              : offer.playerRanking
          }
        </span>

        <div
          className={cn(
            "absolute left-0 top-0 rounded-br px-1.5 text-xs font-medium",
            variant === "blue" && "bg-blue-200 text-blue-500",
            variant === "purple" && "bg-purple-200 text-purple-500",
          )}
        >
          {offer.playerRankingType}
        </div>
        <div
          className={cn(
            "absolute bottom-0 right-0 text-nowrap rounded-tl px-1.5 text-xs font-medium",
            variant === "blue" && "bg-blue-200 text-blue-500",
            variant === "purple" && "bg-purple-200 text-purple-500",
          )}
        >
          {t("profile:or-more")}
        </div>
      </div>

      <div className="flex flex-1 items-center justify-center">
        <span className="text-nowrap text-sm font-semibold text-blue-500">
          {offer.matchPrice ? `${offer.matchPrice.toLocaleString()} €` : t("profile:define-price")}
        </span>
      </div>

      {(userRole !== "player" || user.isAdmin) && <EditIcon className="size-4 shrink-0 text-neutral-500 sm:block" />}
    </button>
  );
};

const MatchOfferForm = ({ matchOffer, gender, clubRepresentativeId, closeModal }: { matchOffer?: RouterOutputs["club"]["getClub"]["matchOffers"][number];gender: "male" | "female"; clubRepresentativeId: string; closeModal: () => void }) => {
  const { t } = useTranslation(["common", "profile"]);
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const { mutate, isPending } = useMutation(trpc.club.upsertMatchOffer.mutationOptions({
    onSuccess: () => {
      queryClient.invalidateQueries(trpc.club.getClub.queryFilter());
      closeModal();
    },
  }));

  const { mutate: deleteOffer, isPending: isDeleting } = useMutation(trpc.club.deleteMatchOffer.mutationOptions({
    onSuccess: () => {
      queryClient.invalidateQueries(trpc.club.getClub.queryFilter());
      closeModal();
    },
  }));

  const matchOfferForm = useForm<MatchOfferSchema>({
    resolver: zodResolver(matchOfferSchema),
    defaultValues: {
      id: matchOffer?.id || undefined,
      playerGender: matchOffer?.playerGender || gender,
      playerRankingType: matchOffer?.playerRankingType || undefined,
      playerRanking: matchOffer?.playerRanking || undefined,
      playerRankingNumber: matchOffer?.playerRankingNumber || undefined,
      matchPrice: matchOffer?.matchPrice || undefined,
    },
  });

  const onSubmit = matchOfferForm.handleSubmit((data) => {
    mutate({ ...data, clubRepresentativeId });
  });

  const onDeleteOffer = () => {
    if (!matchOffer)
      return;
    deleteOffer({ id: matchOffer.id });
  };

  return (
    <>
      <SheetForm id="MatchOfferForm" onSubmit={onSubmit}>
        <Controller
          control={matchOfferForm.control}
          name="playerRankingType"
          rules={{
            onChange: (event) => {
              if (event) {
                matchOfferForm.setValue("playerRanking", "");
                matchOfferForm.setValue("playerRankingNumber", undefined);
                matchOfferForm.clearErrors("playerRanking");
              }
            },
          }}
          render={({ field, fieldState }) => (
            <RootField>
              <Label>{t("common:ranking-type")}</Label>
              <Select value={field.value || undefined} onValueChange={field.onChange}>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    {rankingTypes.map(type => (
                      <SelectItem key={type} value={type}>
                        {type}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
              {fieldState.error && (
              // @ts-expect-error Error type on null value
                <ErrorField>{t(fieldState.error.message)}</ErrorField>
              )}
            </RootField>
          )}
        />

        {/* FFT Ranking */}
        {matchOfferForm.watch("playerRankingType") === "FFT"
        && (
          <div className="flex flex-col gap-4">
            <Controller
              control={matchOfferForm.control}
              name="playerRanking"
              rules={{
                onChange: (event) => {
                  if (event) {
                    matchOfferForm.setValue("playerRankingNumber", undefined);
                  }
                },
              }}
              render={({ field, fieldState }) => (
                <RootField>
                  <Label>
                    {t("profile:ranking-wanted")}
                  </Label>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        {playerFFTranking.map(ranking => (
                          <SelectItem key={ranking} value={ranking}>{ranking === "T100" ? `${t("profile:numbered")} - T100` : ranking}</SelectItem>
                        ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                  {fieldState.error?.message && (
                  // @ts-expect-error Error type on null value
                    <ErrorField>{t(fieldState.error.message)}</ErrorField>
                  )}
                </RootField>
              )}
            />

            {matchOfferForm.watch("playerRanking") === "T100" && (
              <TextField
                type="number"
                helper={t("profile:ranking-numbered-helper")}
                prefix={<span className="text-sm font-bold">N</span>}
                label={t("profile:numbered")}
                max={100}
                min={1}
                {...matchOfferForm.register("playerRankingNumber")}
                errorMessage={
                  // @ts-expect-error Error type on null value
                  matchOfferForm.formState.errors.playerRankingNumber?.message ? t(matchOfferForm.formState.errors.playerRankingNumber.message) : undefined
                }
              />
            )}
          </div>
        )}

        {/* UTR Ranking */}
        {matchOfferForm.watch("playerRankingType") === "UTR" && (
          <Controller
            name="playerRanking"
            control={matchOfferForm.control}
            render={({ field }) => (
              <TextField
                helper="ex: 12.34, 1.23"
                label={t("profile:ranking-wanted")}
                value={field.value || ""}
                onChange={(event) => {
                  field.onChange(event.target.value);
                }}
                errorMessage={
                  // @ts-expect-error Error type on null value
                  matchOfferForm.formState.errors.playerRanking?.message ? t(matchOfferForm.formState.errors.playerRanking.message) : undefined
                }
              />
            )}
          />
        )}

        {/* ATP/WTA/ITF Ranking */}
        {
          ["ATP", "WTA", "ITF"].includes(matchOfferForm.watch("playerRankingType"))
          && (
            <TextField
              type="number"
              helper={t("profile:ranking-ATP-WTA-ITF-helper")}
              label={t("profile:ranking-wanted")}
              max={2000}
              min={1}
              {...matchOfferForm.register("playerRanking")}
              errorMessage={
                // @ts-expect-error Error type on null value
                matchOfferForm.formState.errors.playerRanking?.message ? t(matchOfferForm.formState.errors.playerRanking.message) : undefined
              }
            />
          )
        }

        {
          !matchOfferForm.watch("playerRankingType")
          && (
          // Need this disabled field to act like a placeholder when rankingType is not selected yet
            <TextField
              label={t("profile:ranking-wanted")}
              readOnly
              className="pointer-events-none"
              disabled
              errorMessage={
                // @ts-expect-error Error type on null value
                matchOfferForm.formState.errors.playerRanking?.message ? t(matchOfferForm.formState.errors.playerRanking.message) : undefined
              }
            />
          )
        }

        <TextField
          type="number"
          helper={t("profile:price-helper")}
          label={t("profile:match-price")}
          min={0}
          {...matchOfferForm.register("matchPrice")}
          disabled={!matchOfferForm.watch("playerRanking")}
          errorMessage={
            // @ts-expect-error Error type on null value
            matchOfferForm.formState.errors.matchPrice?.message ? t(matchOfferForm.formState.errors.matchPrice.message) : undefined
          }
        />
      </SheetForm>

      {matchOffer && (
        <Button
          type="button"
          variant="ghost"
          className="text-white"
          onClick={onDeleteOffer}
        >
          {t("profile:delete-offer")}
        </Button>
      )}

      <SheetFooter>
        <SheetClose asChild>
          <Button variant="dark" type="button">
            {t("common:cancel")}
          </Button>
        </SheetClose>
        <Button type="submit" form="MatchOfferForm" loading={isDeleting || isPending || matchOfferForm.formState.isSubmitting}>
          {t("common:save")}
        </Button>
      </SheetFooter>
    </>
  );
};

export function ClubMatchOffers({ matchOffers, clubRepresentativeId }: { matchOffers?: RouterOutputs["club"]["getClub"]["matchOffers"]; clubRepresentativeId: string }) {
  const { t } = useTranslation(["common", "profile"]);
  const [modalState, updateModalState] = useState<"man" | "woman" | "closed">("closed");
  const isModalOpen = modalState !== "closed";
  const [gender, setGender] = useState<"male" | "female">("male");
  const [matchOffer, setMatchOffer] = useState<RouterOutputs["club"]["getClub"]["matchOffers"][number] | undefined>(undefined);
  const userRole = useUserRole();
  const { user } = useMe();

  const menMatchOffers = matchOffers?.filter(offer => offer.playerGender === "male");
  const womenMatchOffers = matchOffers?.filter(offer => offer.playerGender === "female");

  return (
    <div className="flex w-full flex-col gap-1">
      <div className="flex w-full flex-col gap-1 rounded-xl border border-neutral-400 p-1.5">
        <div className="flex h-9 w-full items-center justify-between px-2">
          <div className="flex h-9 items-center gap-1 text-neutral-900">
            <CupIcon />
            <span className="text-sm font-medium">{t("profile:match")}</span>
          </div>

          <span className="text-sm font-medium text-orange-500">{t("profile:players-wanted")}</span>
        </div>

        <div className="flex gap-1.5">
          <div className="flex size-full flex-col gap-1.5">
            <Badge variant="blue" className="mx-auto shrink-0 text-sm">{t("common:men")}</Badge>

            {[0, 1].map((index) => {
              const offer = menMatchOffers?.[index];
              return offer
                ? (
                    <div className="flex w-full flex-1" key={index}>
                      <OfferButton
                        key={index}
                        offer={offer}
                        variant="blue"
                        onClick={() => {
                          setMatchOffer(offer);
                          updateModalState("man");
                        }}
                      />
                    </div>
                  )
                : (
                    <div className="flex flex-1" key={index}>
                      { (userRole !== "player" || user.isAdmin)
                        ? (
                            <EmptyButton
                              key={index}
                              onClick={() => {
                                updateModalState("man");
                                setGender("male");
                              }}
                            />
                          )
                        : (
                            <div className="flex h-16 w-full flex-1 items-center justify-center rounded-lg bg-neutral-50 p-1">
                              <div className="flex size-full items-center justify-center rounded-md bg-white">
                                <DotsIcon />
                              </div>
                            </div>
                          )}
                    </div>
                  );
            })}
          </div>

          <div className="flex size-full flex-col gap-1.5">
            <Badge variant="purple" className="mx-auto shrink-0 text-sm">{t("common:women")}</Badge>

            {[0, 1].map((index) => {
              const offer = womenMatchOffers?.[index];
              return offer
                ? (
                    <div className="flex w-full flex-1" key={index}>
                      <OfferButton
                        key={index}
                        offer={offer}
                        variant="purple"
                        onClick={() => {
                          setMatchOffer(offer);
                          updateModalState("woman");
                        }}
                      />
                    </div>
                  )
                : (
                    <div className="flex w-full flex-1" key={index}>
                      {(userRole !== "player" || user.isAdmin)
                        ? (
                            <EmptyButton
                              key={index}
                              onClick={() => {
                                updateModalState("woman");
                                setGender("female");
                              }}
                            />
                          )
                        : (
                            <div className="flex h-16 w-full flex-1 items-center justify-center rounded-lg bg-neutral-50 p-1">
                              <div className="flex size-full items-center justify-center rounded-md bg-white">
                                <DotsIcon />
                              </div>
                            </div>
                          )}
                    </div>
                  );
            })}
          </div>
        </div>

      </div>

      { userRole === "player" && !user.isAdmin
      && (
        <span className="mx-auto text-center text-xs text-neutral-500">
          {t("common:price-per-meeting")}
        </span>
      )}

      {(userRole !== "player" || user.isAdmin)
      && (
        <Sheet open={isModalOpen} onOpenChange={() => updateModalState("closed")}>
          <SheetContent>
            <SheetHeader>
              <SheetTitle>
                {isModalOpen && `${t("profile:player-wanted")} ${t(`common:${modalState}`)}`}
              </SheetTitle>
            </SheetHeader>

            <MatchOfferForm
              matchOffer={matchOffer}
              gender={gender}
              clubRepresentativeId={clubRepresentativeId}
              closeModal={() => {
                updateModalState("closed");
                setMatchOffer(undefined);
              }}
            />

          </SheetContent>
        </Sheet>
      )}
    </div>
  );
}
